import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Users, 
  Building2, 
  GitBranch, 
  FileText, 
  TrendingUp,
  Clock,
  Shield,
  Activity,
  RefreshCw
} from 'lucide-react'
import { getAuditLogs } from '../utils/auditLogger'
import { dataService } from '../../services/dataService'

const DashboardPage = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  
  // Загружаем актуальные данные напрямую из базы данных
  const loadData = async (isMounted = () => true) => {
    try {
      setIsLoading(true)
      
      // Используем новый централизованный метод ожидания
      await dataService.waitForInitialization()
      
      if (!isMounted()) return // Проверка что компонент не размонтирован

      // Читаем данные из серверного API через dataService
      const departments = await dataService.getDepartments()
      const positions = await dataService.getPositions()  
      const transitions = await dataService.getTransitions()
      
      if (!isMounted()) return // Финальная проверка перед setState
      
      setDashboardData({
        departments,
        positions,
        transitions,
        isLive: true
      })
      setIsLoading(false)
    } catch (error) {
      console.error('Ошибка загрузки данных дашборда:', error)
      
      if (!isMounted()) return
      
      // Более детальная обработка ошибок
      if (error.message.includes('таймаут')) {
        // Показываем специальное сообщение для таймаутов
        console.error('⏰ Таймаут загрузки данных дашборда')
      }
      
      setIsLoading(false)
    }
  }
  
  useEffect(() => {
    let isMounted = true
    
    const loadDataSafe = () => loadData(() => isMounted)
    loadDataSafe()
    
    // Cleanup function
    return () => {
      isMounted = false
    }
  }, [])
  
  // Обновление данных
  const refreshData = () => {
    loadData(() => true) // При ручном обновлении всегда выполняем
  }
  
  if (isLoading || !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Загружаю данные...</span>
        </div>
      </div>
    )
  }

  // Подсчет статистики из данных базы
  const totalDepartments = dashboardData.departments.length
  const totalPositions = dashboardData.positions.length
  const totalTransitions = dashboardData.transitions.length
  const recentLogs = getAuditLogs().slice(0, 5)

  const stats = [
    {
      title: 'Отделы',
      value: totalDepartments,
      icon: Building2,
      color: 'bg-blue-500',
      description: 'Всего отделов в организации'
    },
    {
      title: 'Должности',
      value: totalPositions,
      icon: Users,
      color: 'bg-green-500',
      description: 'Общее количество должностей'
    },
    {
      title: 'Карьерные пути',
      value: totalTransitions,
      icon: GitBranch,
      color: 'bg-purple-500',
      description: 'Доступных переходов'
    },
    {
      title: 'Записей в логе',
      value: getAuditLogs().length,
      icon: FileText,
      color: 'bg-orange-500',
      description: 'Всего операций в аудит-логе'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Дашборд</h2>
          <p className="text-gray-600 mt-1">
            Обзор системы Карьерной Матрицы HR
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={refreshData}
            className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Обновить</span>
          </button>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>Обновлено: {new Date().toLocaleString('ru-RU')}</span>
          </div>
        </div>
      </motion.div>

      {/* Карточки статистики */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            
            <div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <h3 className="font-semibold text-gray-700 mb-1">{stat.title}</h3>
              <p className="text-sm text-gray-500">{stat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Последние действия */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-blue-500" />
              Последние действия
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700">
              Все логи →
            </button>
          </div>
          
          <div className="space-y-3">
            {recentLogs.length > 0 ? (
              recentLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {log.action.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {log.formattedTime} • {log.user}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Пока нет записей в логе</p>
                <p className="text-xs">Действия будут отображаться здесь</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Распределение должностей по отделам */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-green-500" />
            Распределение должностей
          </h3>
          
          <div className="space-y-3">
            {dashboardData.departments.map((dept, index) => {
              const departmentPositions = dashboardData.positions.filter(pos => pos.department_id === dept.id)
              const positionsCount = departmentPositions.length
              const percentage = totalPositions > 0 ? (positionsCount / totalPositions * 100) : 0
              
              return (
                <div key={dept.id || dept.name || `dashboard-dept-${index}`} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700 truncate">
                      {dept.name}
                    </span>
                    <span className="text-gray-500">
                      {positionsCount} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ delay: 0.6 + Math.min(index * 0.1, 1.0), duration: 0.5 }}
                      className="h-2 rounded-full"
                      style={{ backgroundColor: dept.color || '#3B82F6' }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </motion.div>
      </div>

      {/* Быстрые действия */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-purple-500" />
          Быстрые действия
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button onClick={() => navigate('/admin/departments')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <Users className="w-6 h-6 text-blue-500 mb-2" />
            <div className="font-medium text-gray-900">Добавить должность</div>
            <div className="text-xs text-gray-500">Создать новую позицию</div>
          </button>
          
          <button onClick={() => navigate('/admin/transitions')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <GitBranch className="w-6 h-6 text-green-500 mb-2" />
            <div className="font-medium text-gray-900">Настроить переходы</div>
            <div className="text-xs text-gray-500">Карьерные пути</div>
          </button>
          
          <button onClick={() => navigate('/admin/settings')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <FileText className="w-6 h-6 text-orange-500 mb-2" />
            <div className="font-medium text-gray-900">Экспорт данных</div>
            <div className="text-xs text-gray-500">Выгрузить отчет</div>
          </button>
          
          <button onClick={() => navigate('/admin/audit')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
            <Activity className="w-6 h-6 text-purple-500 mb-2" />
            <div className="font-medium text-gray-900">Просмотр логов</div>
            <div className="text-xs text-gray-500">Аудит действий</div>
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default DashboardPage 